import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getErrorMessage } from '../api/client';
import { getMyWasteToday, getTeamWasteToday, getWasteOptions, markWasteReviewed, reverseWaste, setWasteThreshold } from '../api/waste';
import { useAuth } from '../auth/AuthProvider';
import { canConfigureWasteThreshold, canReviewWaste } from '../auth/permissions';
import { Answers, ChoiceCards, FooterReason, Question } from '../components/capture';
import { Modal } from '../components/Modal';
import { VoidRequestModal } from '../components/VoidRequestModal';
import { WasteCaptureModal } from '../components/WasteCaptureModal';
import { WasteReportPanel } from '../components/WasteReportPanel';
import { WasteReviewPanel } from '../components/WasteReviewPanel';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { EmptyState } from '../components/ui/EmptyState';
import { Icon } from '../components/ui/Icon';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionTabs } from '../components/ui/SectionTabs';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Button, LoadingPanel } from '../components/ui/primitives';
import type { WasteCaptureItem, WasteCaptureOptions, WasteReviewItem, WasteTeamToday, WasteThreshold, WasteThresholdMode } from '../types/waste';
import { resolveStoreTimezone } from '../utils/timezones';
import { parseDecimalInput, type PendingWasteCapture } from '../utils/wasteCapture';
import { WASTE_CATEGORY_LABELS } from '../utils/wasteCategories';
import { readPendingWaste, writePendingWaste } from '../utils/wastePendingStore';
import { reviewedText } from '../utils/wasteReport';
import {
  WASTE_REVERSAL_REASONS, formatWasteQuantity, formatWasteTime, sortWasteForReview, thresholdSummary, thresholdUncostedToSend, wasteFlagBadges,
  wasteMeasureText, wasteThresholdBlocker,
} from '../utils/wasteReview';

// F1 · pantalla «Mermas», FUERA de Inventario. El EMPLOYEE registra y ve SUS mermas de hoy (sin
// costos ni existencias). OWNER/MANAGER además revisan la bandeja «Mermas de hoy» de sus tiendas,
// anulan con un movimiento de reversa y (solo el dueño) ajustan cuándo una merma es «grande».

const money = (value: string) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value));
// F2: pestañas del gerente.
const WASTE_TABS = [['today', 'Hoy'], ['review', 'Por revisar'], ['report', 'Reporte']] as const;
type WasteTab = (typeof WASTE_TABS)[number][0];
const errorCode = (error: unknown) => (error instanceof ApiError ? (error.payload as { error?: string } | undefined)?.error : undefined);

export function WastePage() {
  const { user, stores: authStores } = useAuth();
  const confirm = useConfirm();
  const reviewer = canReviewWaste(user?.role);
  const owner = canConfigureWasteThreshold(user?.role);
  const [options, setOptions] = useState<WasteCaptureOptions>();
  const [mine, setMine] = useState<WasteCaptureItem[]>();
  const [team, setTeam] = useState<WasteTeamToday>();
  const [storeFilter, setStoreFilter] = useState('');
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [captureOpen, setCaptureOpen] = useState(false);
  // Captura cuyo envío quedó en duda: se retoma con la misma llave (no se duplica). Vive también en el
  // navegador, así sobrevive a salir de la sección o recargar la página.
  const [pendingCapture, setPendingCapture] = useState<PendingWasteCapture | null>(() => readPendingWaste(user?.id));
  const [reverseTarget, setReverseTarget] = useState<WasteReviewItem | null>(null);
  const [thresholdOpen, setThresholdOpen] = useState(false);
  // F2: pestañas del gerente, «Pedir anulación» del empleado y «Ya la revisé».
  const [tab, setTab] = useState<WasteTab>('today');
  // Las pestañas ya abiertas se quedan montadas (ocultas) para no perder sus fechas ni su tienda.
  const [visitedTabs, setVisitedTabs] = useState<Set<WasteTab>>(() => new Set<WasteTab>(['today']));
  const openTab = (value: WasteTab) => {
    setTab(value);
    setVisitedTabs((previous) => (previous.has(value) ? previous : new Set(previous).add(value)));
  };
  const [voidTarget, setVoidTarget] = useState<WasteCaptureItem | null>(null);
  const [reviewingId, setReviewingId] = useState<string>();
  // Sube cuando una acción de esta pantalla (anular, revisar) cambió lo que muestra «Por revisar».
  const [refreshKey, setRefreshKey] = useState(0);
  // Solo la respuesta de la ÚLTIMA carga se pinta (cambiar de tienda rápido no mezcla resultados).
  const loadSeq = useRef(0);

  useEffect(() => { setPendingCapture(readPendingWaste(user?.id)); }, [user?.id]);
  const rememberPending = (pending: PendingWasteCapture | null) => {
    setPendingCapture(pending);
    writePendingWaste(user?.id, pending);
  };

  const load = useCallback(async () => {
    const current = ++loadSeq.current;
    setLoading(true);
    setError(undefined);
    // Por separado: si la lista falla, registrar merma sigue disponible.
    const [optionsResult, listResult] = await Promise.allSettled([
      getWasteOptions(),
      reviewer ? getTeamWasteToday(storeFilter || undefined) : getMyWasteToday(),
    ]);
    if (current !== loadSeq.current) return;
    if (optionsResult.status === 'fulfilled') setOptions(optionsResult.value);
    if (listResult.status === 'fulfilled') {
      if (reviewer) setTeam(listResult.value as WasteTeamToday);
      else setMine((listResult.value as { items: WasteCaptureItem[] }).items);
    }
    const failure = [optionsResult, listResult].find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failure) setError(getErrorMessage(failure.reason, 'No fue posible cargar las mermas.'));
    setLoading(false);
  }, [reviewer, storeFilter]);

  useEffect(() => {
    void load();
    return () => { loadSeq.current += 1; };
  }, [load]);

  const multiStore = (options?.stores.length ?? authStores.length) > 1;
  // Zona para las fechas por defecto de «Por revisar» y del reporte: la de la tienda, no la del teléfono.
  const defaultTimeZone = resolveStoreTimezone(authStores, { storeId: options?.stores[0]?.id });
  const timeOf = (item: WasteCaptureItem) => formatWasteTime(item.occurredAt, resolveStoreTimezone(authStores, { storeId: item.storeId }));
  const categoryOf = (item: WasteCaptureItem) => (item.category ? WASTE_CATEGORY_LABELS[item.category] ?? item.category : item.legacyReason ?? 'Sin categoría');
  const noStores = options !== undefined && options.stores.length === 0;
  const visibleTeam = team ? sortWasteForReview(team.items).filter((item) => !onlyFlagged || (!item.reversed && item.flags.length > 0)) : [];
  const pendingIngredient = pendingCapture ? options?.ingredients.find((ingredient) => ingredient.id === pendingCapture.draft.ingredientId) : undefined;
  const pendingLabel = pendingCapture && pendingIngredient ? ` (${pendingCapture.draft.quantity.trim()} ${pendingIngredient.unit.symbol} de ${pendingIngredient.name})` : '';

  const openCapture = () => { setNotice(undefined); setCaptureOpen(true); };
  // Descartar a propósito una captura en duda (por ejemplo, porque ya aparece en la lista de hoy).
  const discardPending = async () => {
    const ok = await confirm({
      title: '¿Descartar la merma sin confirmar?',
      description: 'Hazlo si ya la ves en la lista de hoy o si no quieres registrarla. Si después la vuelves a capturar, contará como una merma nueva.',
      confirmLabel: 'Descartar',
      cancelLabel: 'Conservar',
      danger: true,
    });
    if (ok) rememberPending(null);
  };

  // F2: «Ya la revisé» desde la bandeja de hoy (solo mermas marcadas). Repetirlo no duplica.
  const reviewToday = async (item: WasteReviewItem) => {
    if (reviewingId) return;
    setReviewingId(item.id);
    setError(undefined);
    try {
      await markWasteReviewed(item.id);
      setNotice(`Marcada como revisada: ${formatWasteQuantity(item.quantity, item.unitSymbol)} de ${item.ingredientName}.`);
      setRefreshKey((key) => key + 1);
      await load();
    } catch (reviewError) {
      setError(getErrorMessage(reviewError, 'No fue posible marcarla como revisada.'));
    } finally {
      setReviewingId(undefined);
    }
  };

  return (
    <section aria-label="Mermas" className="n3-screen waste-page">
      {/* P-075: el encabezado de la piel nueva (el `.page-header` de antes no tenía estilo). «Cambiar umbral»
          estaba al pie de la bandeja de hoy, después de hasta 200 mermas: ahora va junto a la acción principal
          (solo el dueño; el mismo diálogo, con el umbral que trae la bandeja de hoy). */}
      <PageHeader
        actions={<>
          {owner && <Button disabled={!team} onClick={() => setThresholdOpen(true)} variant="ghost">Cambiar umbral</Button>}
          <Button disabled={!options || noStores} iconStart={<Icon name="plus" size={18} />} onClick={openCapture} variant="primary">Registrar merma</Button>
        </>}
        description={reviewer
          ? 'Lo que se perdió hoy en tus tiendas. Revísalo y anula lo que se registró por error.'
          : 'Registra lo que se perdió. Se descuenta del inventario al momento y tu gerente lo revisa.'}
        title="Mermas"
      />

      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {notice && <div className="alert alert--success" role="status">{notice}</div>}
      {pendingCapture && !captureOpen && (
        <div className="alert alert--warning" role="status">
          Hay una merma cuyo registro no se confirmó{pendingLabel}. Revísala y toca «Registrar merma»: si ya se había guardado, no se duplica.{' '}
          <button className="link-button" disabled={!options || noStores} onClick={openCapture} type="button">Revisarla</button>
          {' · '}
          <button className="link-button" onClick={() => void discardPending()} type="button">Ya la revisé, descartar</button>
        </div>
      )}
      {noStores && (owner
        ? <EmptyState action={<a className="n3-button n3-button--secondary" href="#stores">Ir a Tiendas</a>} compact icon="stores" title="Tu negocio no tiene tiendas activas" description="Activa o da de alta una tienda para poder registrar mermas." />
        : <EmptyState compact icon="stores" title="No tienes una tienda asignada" description={reviewer ? 'Pide al dueño que te asigne una tienda para registrar y revisar mermas.' : 'Pide a tu dueño o gerente que te asigne una tienda para poder registrar mermas.'} />)}
      {loading && !mine && !team && <LoadingPanel label="Cargando mermas…" />}

      {!reviewer && mine && (
        <section className="content-card" aria-label="Mis mermas de hoy">
          <div className="content-card__header"><div><span className="eyebrow">Hoy</span><h2>Mis mermas de hoy</h2></div></div>
          {mine.length === 0
            ? <p className="wiz-hint">Hoy no has registrado mermas.</p>
            : <ul className="pending-rows">
              {mine.map((item) => (
                <li key={item.id}>
                  <div className="pending-row pending-row--static">
                    <span className={`pending-row__ic${item.reversed ? ' pending-row__ic--muted' : ''}`}><Icon name="trash" size={18} /></span>
                    <span className="pending-row__body">
                      <strong>{formatWasteQuantity(item.quantity, item.unitSymbol)} de {item.ingredientName}</strong>
                      <span>{timeOf(item)} · {categoryOf(item)}{multiStore ? ` · ${item.storeName}` : ''}{item.notes ? ` · ${item.notes}` : ''}</span>
                      {item.reversed && <span>Anulada por tu gerente{item.reversalReason ? `: ${item.reversalReason}` : ''}</span>}
                    </span>
                    {item.reversed
                      ? <StatusBadge variant="neutral">Anulada</StatusBadge>
                      : item.pendingVoidRequest
                        ? <StatusBadge variant="warning">Anulación pedida</StatusBadge>
                        // F2: el mismo día, si no se contó después. La decide el gerente en Solicitudes.
                        : item.reversible !== false && <button className="button button--secondary" onClick={() => { setNotice(undefined); setVoidTarget(item); }} type="button">Pedir anulación</button>}
                  </div>
                </li>
              ))}
            </ul>}
        </section>
      )}

      {/* P-075: las vistas con las pestañas de sección (antes, un `segmented`). Las ya abiertas siguen montadas. */}
      {reviewer && <SectionTabs active={tab} label="Vistas de Mermas" onSelect={(id) => openTab(id as WasteTab)} tabs={WASTE_TABS.map(([id, label]) => ({ id, label }))} />}
      {reviewer && visitedTabs.has('review') && (
        <div hidden={tab !== 'review'}>
          <WasteReviewPanel
            categoryOf={categoryOf}
            multiStore={multiStore}
            onChanged={() => void load()}
            onReverse={(item) => { setNotice(undefined); setReverseTarget(item); }}
            refreshKey={refreshKey}
            stores={options?.stores ?? []}
            timeZone={defaultTimeZone}
            timeZoneOf={(storeId) => resolveStoreTimezone(authStores, { storeId })}
          />
        </div>
      )}
      {reviewer && visitedTabs.has('report') && (
        <div hidden={tab !== 'report'}>
          <WasteReportPanel multiStore={multiStore} refreshKey={refreshKey} stores={options?.stores ?? []} timeZone={defaultTimeZone} />
        </div>
      )}

      {reviewer && tab === 'today' && team && (
        <section aria-label="Mermas de hoy" className="content-card">
          <div className="content-card__header">
            <div>
              <span className="eyebrow">Revisión</span>
              <h2>Mermas de hoy</h2>
              <p className="wiz-hint">
                {team.summary.activeCount} merma(s) · {money(team.summary.netValue)}
                {(team.summary.pendingReviewCount ?? team.summary.flaggedCount) > 0 ? ` · ${team.summary.pendingReviewCount ?? team.summary.flaggedCount} para revisar` : ''}
                {team.summary.reversedCount > 0 ? ` · ${team.summary.reversedCount} anulada(s)` : ''}
                {team.summary.uncostedCount > 0 ? ` · ${team.summary.uncostedCount} sin costo registrado` : ''}
                {team.summary.partial ? ' · cifras parciales: hoy hay más de 2,000 mermas' : ''}
              </p>
            </div>
          </div>
          <div className="stock-filters">
            {multiStore && options && (
              <label className="field field--inline"><span>Tienda</span>
                <select onChange={(event) => setStoreFilter(event.target.value)} value={storeFilter}>
                  <option value="">Todas mis tiendas</option>
                  {options.stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
                </select>
              </label>
            )}
            <label className="coverage-check"><input checked={onlyFlagged} onChange={(event) => setOnlyFlagged(event.target.checked)} type="checkbox" />Solo las marcadas para revisar</label>
          </div>
          {visibleTeam.length === 0
            ? <p className="wiz-hint">{onlyFlagged && team.items.length > 0 ? 'No hay mermas marcadas para revisar hoy.' : 'Hoy no se ha registrado ninguna merma.'}</p>
            : <ul className="pending-rows">
              {visibleTeam.map((item) => (
                <li key={item.id}>
                  <div className="pending-row pending-row--static waste-row">
                    <span className={`pending-row__ic${item.reversed ? ' pending-row__ic--muted' : item.flags.length ? ' pending-row__ic--danger' : ''}`}><Icon name={item.flags.length && !item.reversed ? 'warning' : 'trash'} size={18} /></span>
                    <span className="pending-row__body">
                      <strong>{formatWasteQuantity(item.quantity, item.unitSymbol)} de {item.ingredientName}</strong>
                      <span>{timeOf(item)} · {categoryOf(item)} · {item.createdBy?.name ?? 'Sin usuario'}{multiStore ? ` · ${item.storeName}` : ''}</span>
                      <span>{wasteMeasureText(item)}</span>
                      {item.notes && <span>Nota: {item.notes}</span>}
                      {item.reversed && <span>Anulada por {item.reversedBy?.name ?? 'un gerente'}{item.reversalReason ? `: ${item.reversalReason}` : ''}</span>}
                      {!item.reversed && item.flags.length > 0 && (
                        <span className="waste-row__flags">{wasteFlagBadges(item.flags).map((badge) => <StatusBadge key={badge.flag} variant={badge.variant}>{badge.label}</StatusBadge>)}</span>
                      )}
                      {!item.reversed && item.pendingVoidRequest && <span><StatusBadge variant="warning">Quien la registró pidió anularla</StatusBadge></span>}
                      {/* F2: «Ya la revisé» solo en las marcadas, vigentes y capturadas (no las del conteo). */}
                      {!item.reversed && item.flags.length > 0 && item.lockReason !== 'FROM_COUNT' && (item.review
                        ? <span>{reviewedText(item.review, resolveStoreTimezone(authStores, { storeId: item.storeId }))}</span>
                        : <span><button className="link-button" disabled={reviewingId === item.id} onClick={() => void reviewToday(item)} type="button">{reviewingId === item.id ? 'Guardando…' : 'Ya la revisé'}</button></span>)}
                    </span>
                    {item.reversed
                      ? <StatusBadge variant="neutral">Anulada</StatusBadge>
                      : item.reversible === false
                        // Detectada en conteo, o ya contada después: devolverla dejaría más de lo que hay; se corrige volviendo a contar.
                        // P-078 (BK28, Codex R2 · M4): la insignia y, visible, por qué no se anula aquí.
                        ? <span className="waste-lock"><StatusBadge variant="neutral">{item.lockReason === 'COUNTED_AFTER' ? 'Ya contada' : 'Del conteo'}</StatusBadge><small className="waste-lock__why">{item.lockReason === 'COUNTED_AFTER' ? 'Un conteo posterior ya la tomó en cuenta; para corregir, vuelve a contar.' : 'Se reconoció al contar; no se anula aquí.'}</small></span>
                        : <button className="button button--secondary" onClick={() => { setNotice(undefined); setReverseTarget(item); }} type="button">Anular</button>}
                  </div>
                </li>
              ))}
            </ul>}
          {team.truncated && <p className="wiz-hint">Se muestran 200: primero las marcadas para revisar, luego las más recientes.</p>}
          <p className="wiz-hint">{thresholdSummary(team.threshold)}</p>
        </section>
      )}

      {captureOpen && options && (
        <WasteCaptureModal
          initial={pendingCapture}
          options={options}
          reviewer={reviewer}
          onClose={() => { setCaptureOpen(false); void load(); }}
          onSaved={(waste, replayed) => {
            rememberPending(null);
            const what = `${formatWasteQuantity(waste.quantity, waste.unitSymbol)} de ${waste.ingredientName}`;
            setNotice(waste.reversed
              ? `Esa merma ya se había registrado y después se anuló (${what}); no se descontó otra vez.`
              : replayed ? `Esa merma ya estaba registrada y no se duplicó: ${what}.` : `Merma registrada: ${what}.`);
            void load();
          }}
          onAttemptStarted={rememberPending}
          onAttemptFailed={(pending) => { rememberPending(pending); void load(); }}
        />
      )}
      {reverseTarget && (
        <WasteReverseModal
          item={reverseTarget}
          categoryLabel={categoryOf(reverseTarget)}
          onClose={() => setReverseTarget(null)}
          onReversed={() => { setNotice(`Merma anulada: se devolvieron ${formatWasteQuantity(reverseTarget.quantity, reverseTarget.unitSymbol)} de ${reverseTarget.ingredientName} al inventario.`); setReverseTarget(null); setRefreshKey((key) => key + 1); void load(); }}
          onStale={(message) => { setReverseTarget(null); setError(message); setRefreshKey((key) => key + 1); void load(); }}
        />
      )}
      {voidTarget && (
        <VoidRequestModal
          allowModify={false}
          // Al cerrar (incluso tras un rechazo, p. ej. «ya hay una solicitud»), la lista se vuelve a leer.
          onClose={() => { setVoidTarget(null); void load(); }}
          onSubmitted={(message) => { setVoidTarget(null); setNotice(message); void load(); }}
          targetId={voidTarget.id}
          targetSummary={`${formatWasteQuantity(voidTarget.quantity, voidTarget.unitSymbol)} de ${voidTarget.ingredientName} · ${categoryOf(voidTarget)}`}
          targetType="INVENTORY_WASTE"
        />
      )}
      {thresholdOpen && team && (
        <WasteThresholdModal
          current={team.threshold}
          onClose={() => setThresholdOpen(false)}
          onSaved={() => { setThresholdOpen(false); setNotice('Umbral de merma grande actualizado.'); void load(); }}
        />
      )}
    </section>
  );
}

/** Anular = un movimiento de reversa: devuelve exactamente lo descontado; la merma queda como «anulada». */
function WasteReverseModal({ item, categoryLabel, onClose, onReversed, onStale }: { item: WasteReviewItem; categoryLabel: string; onClose: () => void; onReversed: () => void; onStale: (message: string) => void }) {
  const [choice, setChoice] = useState('');
  const [custom, setCustom] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const reason = (choice === 'OTHER' ? custom : choice).trim();
  const blocker = !choice ? 'Elige por qué se anula.' : reason.length < 10 ? 'Escribe por qué se anula (al menos 10 caracteres).' : null;

  const submit = async () => {
    if (isSaving || blocker) return;
    setIsSaving(true);
    setError(undefined);
    try {
      await reverseWaste(item.id, reason);
      onReversed();
    } catch (reverseError) {
      // Otro gerente ya la anuló (o ya no existe): se refresca la bandeja en vez de dejar «Anular» vivo.
      const code = errorCode(reverseError);
      if (code === 'WASTE_ALREADY_REVERSED' || code === 'WASTE_NOT_FOUND' || code === 'WASTE_FROM_COUNT' || code === 'WASTE_COUNTED_AFTER') { onStale(getErrorMessage(reverseError, 'Esta merma ya no se puede anular.')); return; }
      setError(getErrorMessage(reverseError, 'No fue posible anular la merma.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title="Anular merma"
      description={`${formatWasteQuantity(item.quantity, item.unitSymbol)} de ${item.ingredientName} · ${categoryLabel}`}
      isBusy={isSaving}
      onClose={onClose}
      footer={<>
        <FooterReason>{blocker}</FooterReason>
        <button className="text-button" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
        <button className="button button--danger" disabled={isSaving || Boolean(blocker)} onClick={() => void submit()} type="button">{isSaving ? 'Anulando…' : 'Anular merma'}</button>
      </>}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <p>Se devolverán {formatWasteQuantity(item.quantity, item.unitSymbol)} al inventario. La merma queda en el historial como anulada; no se borra.</p>
      <Question>¿Por qué se anula?</Question>
      <ChoiceCards ariaLabel="Motivo" onChange={setChoice} options={[...WASTE_REVERSAL_REASONS.map((value) => ({ value, label: value })), { value: 'OTHER', label: 'Otro motivo' }]} value={choice} variant="chips" />
      {choice === 'OTHER' && <Answers><label className="field"><span>Motivo</span><textarea maxLength={300} onChange={(event) => setCustom(event.target.value)} rows={2} value={custom} /></label></Answers>}
      <p className="wiz-hint">El motivo lo verá quien registró la merma.</p>
    </Modal>
  );
}

/** Solo el dueño: en pesos o en porcentaje de lo que había. Nunca bloquea una merma; solo la marca. */
function WasteThresholdModal({ current, onClose, onSaved }: { current: WasteThreshold; onClose: () => void; onSaved: () => void }) {
  const [mode, setMode] = useState<WasteThresholdMode>(current.mode);
  const [value, setValue] = useState(String(current.value));
  const [uncosted, setUncosted] = useState(String(current.uncostedPercentage));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  // Solo se valida lo que está a la vista: en modo porcentaje el campo de insumos sin costo no se ve.
  const blocker = wasteThresholdBlocker(mode, value, uncosted);

  const submit = async () => {
    const numeric = parseDecimalInput(value, 2);
    if (isSaving || blocker || numeric === null) return;
    setIsSaving(true);
    setError(undefined);
    try {
      // Siempre se manda el porcentaje de respaldo, para no perderlo al cambiar de modo.
      await setWasteThreshold({ mode, value: numeric, uncostedPercentage: thresholdUncostedToSend(mode, uncosted, current.uncostedPercentage) });
      onSaved();
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible guardar el umbral.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title="¿Cuándo una merma es grande?"
      description="Las mermas grandes se marcan para revisión; nunca se bloquean."
      isBusy={isSaving}
      onClose={onClose}
      footer={<>
        <FooterReason>{blocker}</FooterReason>
        <button className="text-button" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
        <button className="button button--primary" disabled={isSaving || Boolean(blocker)} onClick={() => void submit()} type="button">{isSaving ? 'Guardando…' : 'Guardar'}</button>
      </>}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <Question>¿Cómo quieres medirla?</Question>
      <ChoiceCards ariaLabel="Cómo medir" onChange={setMode} options={[{ value: 'AMOUNT', label: 'En pesos', hint: 'Ej. desde $200' }, { value: 'PERCENTAGE', label: 'En porcentaje', hint: 'De lo que había del insumo' }]} value={mode} />
      <Answers>
        <label className="field"><span>{mode === 'AMOUNT' ? 'Desde cuántos pesos' : 'Desde qué porcentaje de lo que había'}</span><input inputMode="decimal" onChange={(event) => setValue(event.target.value)} type="text" value={value} /></label>
        {mode === 'AMOUNT' && <label className="field"><span>Si el insumo no tiene costo registrado, desde qué porcentaje de lo que había</span><input inputMode="decimal" onChange={(event) => setUncosted(event.target.value)} type="text" value={uncosted} /></label>}
      </Answers>
      <p className="wiz-hint">En la bandeja cada merma muestra los dos números: su valor y el porcentaje de lo que había.</p>
    </Modal>
  );
}
